import { PluginConfigType } from 'Core/PluginCore/PluginsManager'
import { Studio } from 'Core/Studio'
import { getSummarisationQualityScore } from 'HTTPClients/plugins.client'
import { SingleValueDataType, ValueTypeDefinitions } from 'Types/Tasks.types.ts'
//@ts-ignore
import h2p from 'html2plaintext'
import { convertMarkdownToHTML } from 'Utils/markdown.util'
import { BasePlugin } from 'Core/PluginCore/BasePlugin'
import { PluginConnector } from 'Core/PluginCore/PluginConnector'
import { Signal } from '@preact/signals-react'
import { BlockStateFormatKey, ITaskBlock } from 'shared/express-interface/blocks-core/ITaskBlock'

export type SummarisationQualityType = 'Low' | 'Medium' | 'High'

export type SummarisationQualityPluginConfigType = {
    low_threshold: number[]
    medium_threshold: number[]
    high_threshold: number[]
    scoring_method: string
}

export class SummarisationQualityPlugin extends BasePlugin<
    PluginConfigType<SummarisationQualityPluginConfigType>
> {
    public readonly id = 'Summarisation Quality'

    constructor(
        config: PluginConfigType<SummarisationQualityPluginConfigType>,
        pluginConnectors: PluginConnector[]
    ) {
        super(config, pluginConnectors)
    }

    public async initialize(studio: Studio) {
        this.studio = studio

        this.pluginConnectors.forEach(connector => {
            const data = connector.getData()
            const connectedBlocks = connector.getConnectedBlocks()
            if (data['text']) {
                data['text'].subscribe(() => {
                    this.handleEvent(data, connectedBlocks)
                })
            }
        })
    }

    private async handleEvent(
        data: Record<string, Signal<unknown> | null>,
        connectedBlocks: ITaskBlock[]
    ) {
        if (!data['text']) {
            return
        }

        const value = data['text'].value
        const type = data['text'].key as BlockStateFormatKey
        const text = h2p(
            type === BlockStateFormatKey.Markdown ? convertMarkdownToHTML(value as string) : value
        )

        const summarisationQuality = this.computeSummarisationQuality(
            h2p(this.getPromptContent()),
            text,
            this.config.config.scoring_method
        )
        const summarisationQualityDetails = this.getSummarisaionQualityDetails(summarisationQuality)
        this.handleAnalysisItem(summarisationQualityDetails, connectedBlocks)
    }

    private getPromptContent = () => {
        const taskPrompt = this.studio?.taskManager.getPromptContent()
        if (typeof taskPrompt?.value === 'string') {
            return taskPrompt?.value as string
        } else {
            let htmlContentInPrompt = ''
            ;(taskPrompt?.value as SingleValueDataType<keyof ValueTypeDefinitions>[][]).forEach(
                item => {
                    if (item[0].value_type === 'HTML' || item[0].value_type === 'STRING') {
                        htmlContentInPrompt += item[0].value
                    }
                    if (item[0].value_type === 'MARKDOWN') {
                        htmlContentInPrompt += convertMarkdownToHTML(item[0].value as string)
                    }
                }
            )
            return htmlContentInPrompt
        }
    }
    private computeSummarisationQuality(prompt: string, text: string, modelUsed: string): number {
        if (!text) {
            return 0
        }
        return getSummarisationQualityScore(prompt, text, modelUsed)
    }

    private getSummarisationQualityColorRangeMap(): Record<
        SummarisationQualityType,
        { min: number; max: number; color: `#${string}` }
    > {
        return {
            Low: {
                min: this.config.config.low_threshold[0],
                max: this.config.config.low_threshold[1],
                color: '#ed5b5b',
            },
            Medium: {
                min: this.config.config.medium_threshold[0],
                max: this.config.config.medium_threshold[1],
                color: '#8a8a41',
            },
            High: {
                min: this.config.config.high_threshold[0],
                max: this.config.config.high_threshold[1],
                color: '#3b6a3b',
            },
        }
    }

    private getSummarisaionQualityDetails(summarisationQuality: number) {
        const SummarisationKvp = Object.entries(this.getSummarisationQualityColorRangeMap()).find(
            rc => {
                const [, attrs] = rc
                return attrs.max > summarisationQuality && attrs.min <= summarisationQuality
            }
        )
        if (summarisationQuality === 1) {
            return { color: this.getSummarisationQualityColorRangeMap().High.color, label: 'High' }
        }

        if (!SummarisationKvp) {
            return undefined
        }
        return { color: SummarisationKvp[1].color, label: SummarisationKvp[0] }
    }

    private handleAnalysisItem(
        summarisationQualityDetails: { color: `#${string}`; label: string } | undefined,
        connectedBlocks: ITaskBlock[]
    ) {
        if (summarisationQualityDetails) {
            connectedBlocks.forEach(block => {
                block.setTags?.('summarisation quality', {
                    label: 'Summarisation quality',
                    value: summarisationQualityDetails.label,
                    colorHex: summarisationQualityDetails.color,
                })
            })
        } else {
            connectedBlocks.forEach(block => {
                block.setTags?.('summarisation quality', {
                    label: 'Summarisation quality',
                    value: 'Low',
                    colorHex: this.getSummarisationQualityColorRangeMap().Low.color,
                })
            })
        }
    }
}
